import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Organization {
  id: string;
  name: string;
  role: string;
  is_primary: boolean;
}

interface User {
  user_id: string;
  email: string;
  name: string;
  organization_id: string;
  organization_name: string;
  role: string;
  organizations?: Organization[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
  canWrite: () => boolean;
  canRead: () => boolean;
  isViewer: () => boolean;
  isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// ============================================================================
// Window-specific token storage helpers
// Exported for use in other components (e.g., OrganizationSwitcher)
// ============================================================================

// Helper: Get or create window-specific ID
export const getWindowId = (): string => {
  let windowId = sessionStorage.getItem('leanai_window_id');
  if (!windowId) {
    windowId = crypto.randomUUID();
    sessionStorage.setItem('leanai_window_id', windowId);
  }
  return windowId;
};

// Helper: Get token for current window
export const getTokenForWindow = (): string | null => {
  const windowId = getWindowId();
  return localStorage.getItem(`leanai_token_${windowId}`);
};

// Helper: Find any valid token from localStorage (for tab reuse)
// Uses a lock to prevent race conditions when multiple tabs open simultaneously
export const findAnyValidToken = (): string | null => {
  // Simple lock mechanism using localStorage
  const lockKey = 'leanai_token_lookup_lock';
  const lockTimeout = 1000; // 1 second timeout
  const now = Date.now();

  // Try to acquire lock
  const existingLock = localStorage.getItem(lockKey);
  if (existingLock) {
    const lockTime = parseInt(existingLock, 10);
    // If lock is too old, ignore it (previous tab crashed or didn't clean up)
    if (now - lockTime < lockTimeout) {
      // Lock is held, wait a bit and try again
      return null;
    }
  }

  // Acquire lock
  localStorage.setItem(lockKey, now.toString());

  try {
    // Look for any existing token
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('leanai_token_')) {
        const token = localStorage.getItem(key);
        if (token) {
          return token;
        }
      }
    }
    return null;
  } finally {
    // Always release lock
    localStorage.removeItem(lockKey);
  }
};

// Helper: Set token for current window
export const setTokenForWindow = (token: string): void => {
  const windowId = getWindowId();
  localStorage.setItem(`leanai_token_${windowId}`, token);
};

// Helper: Clear token for current window
export const clearTokenForWindow = (): void => {
  const windowId = getWindowId();
  localStorage.removeItem(`leanai_token_${windowId}`);
};

// Helper: Clean up orphaned tokens from localStorage
// NOTE: This function is intentionally conservative - it only removes tokens on logout
// We CANNOT safely determine which windows are still open from another window,
// so we don't do periodic cleanup to avoid breaking other active sessions
export const cleanupOrphanedTokens = (): void => {
  // Only clean up when explicitly called on logout
  // This prevents accidentally removing tokens from other open windows
  const currentWindowId = getWindowId();
  const currentTokenKey = `leanai_token_${currentWindowId}`;

  // Only remove THIS window's token
  localStorage.removeItem(currentTokenKey);
  console.log('Cleaned up token for closed window');
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize auth state from window-specific localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check for iframe_token in URL (chatbot iframe embedding)
        const urlParams = new URLSearchParams(window.location.search);
        const iframeToken = urlParams.get('iframe_token');

        if (iframeToken) {
          // Always clean token from URL to prevent retry loops on refresh
          const url = new URL(window.location.href);
          url.searchParams.delete('iframe_token');
          window.history.replaceState({}, '', url.toString());

          try {
            const API_BASE_URL = process.env.REACT_APP_API_URL;
            if (!API_BASE_URL) {
              console.warn('REACT_APP_API_URL not set — cannot exchange iframe token');
            } else {
              const res = await fetch(`${API_BASE_URL}/api/auth/iframe-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ iframe_token: iframeToken }),
              });
              if (res.ok) {
                const data = await res.json();
                setUser(data.user);
                setToken(data.token);
                setTokenForWindow(data.token);
                setError(null);
                return; // skip normal localStorage flow
              }
            }
          } catch (err) {
            console.warn('Iframe token exchange failed, falling back to normal auth', err);
          }
          // Fall through to normal auth if iframe exchange failed
        }

        // First, try to get token for this window
        let savedToken = getTokenForWindow();

        // If no token for this window, try to reuse any existing valid token
        if (!savedToken) {
          savedToken = findAnyValidToken();
          if (savedToken) {
            // Reuse existing token for this new window
            setTokenForWindow(savedToken);
          }
        }

        if (savedToken) {
          await validateAndSetToken(savedToken);
        }
      } catch (err) {
        clearTokenForWindow();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const validateAndSetToken = async (authToken: string) => {
    try {
      // Require explicit API URL configuration - fail fast if not set
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      console.log('DEBUG: REACT_APP_API_URL =', API_BASE_URL);
      if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_URL environment variable must be set');
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(authToken);
        setError(null);
        return true;
      } else {
        throw new Error('Invalid token');
      }
    } catch (err) {
      setError('Authentication failed');
      setUser(null);
      setToken(null);
      clearTokenForWindow();
      return false;
    }
  };

  const login = async (authToken: string) => {
    setLoading(true);
    setError(null);

    try {
      const isValid = await validateAndSetToken(authToken);
      if (isValid) {
        setTokenForWindow(authToken);
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setError(null);
    clearTokenForWindow();
    // Clean up any orphaned tokens when user logs out
    cleanupOrphanedTokens();
  };

  // Note: We don't do periodic token cleanup because we can't safely determine
  // which windows are still open. Tokens are only cleaned up on explicit logout.

  // Permission helper functions matching backend logic
  const canWrite = (): boolean => {
    if (!user) return false;
    return ['admin', 'member'].includes(user.role);
  };

  const canRead = (): boolean => {
    if (!user) return false;
    return ['admin', 'member', 'viewer'].includes(user.role);
  };

  const isViewer = (): boolean => {
    if (!user) return false;
    return user.role === 'viewer';
  };

  const isAdmin = (): boolean => {
    if (!user) return false;
    return user.role === 'admin';
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    loading,
    error,
    canWrite,
    canRead,
    isViewer,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;